import React, { useEffect } from 'react';
import { useLazyQuery, useMutation } from 'react-apollo';
import moment from 'moment';

import {
  UserNotificationQuery,
  UpdateNotificationStatusesMutation,
} from '@services/gql/user';
import { NotificationStatus } from '@/types/user';
import LoadingEllipsis from '@/components/LoadingEllipsis';
import { useAuthService } from '@/contexts/authService';

import useNotifications from '../../hooks/useNotifications';
import NotificationListSection from '../NotificationListSection';

import * as styles from './styles.module.css';

const NotificationList = ({ onNotificationClick }) => {
  const authService = useAuthService();
  const userCookie = authService.getUserCookie();
  const skip = !userCookie || !__CLIENT__;
  const [fetch, { data, loading }] = useLazyQuery(UserNotificationQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { primaryIdentityId: userCookie },
  });

  const [updateNotificationStatuses] = useMutation(
    UpdateNotificationStatusesMutation,
  );

  const {
    setSubmitResultNotificationTimestamp,
    submitResultEvents,
    submitResultsLoading,
    unseenNotifications,
    seenNotifications,
    noNotifications,
    hasRecentlySeenResultSubmission,
  } = useNotifications(data?.user?.notifications);

  const acknowledgeNotifications = () => {
    if (unseenNotifications.length) {
      updateNotificationStatuses({
        variables: {
          notificationIds: unseenNotifications.map((n) => n.id),
          status: NotificationStatus.SEEN,
        },
      });
    }
    if (!hasRecentlySeenResultSubmission) {
      setSubmitResultNotificationTimestamp(moment.utc().valueOf());
    }
  };

  const onNotificationClickAugmented = (notificationId) => {
    if (notificationId) {
      updateNotificationStatuses({
        variables: {
          notificationIds: [notificationId],
          status: NotificationStatus.READ,
        },
      });
    }
    onNotificationClick();
  };

  useEffect(() => {
    if (!skip) {
      fetch();
    }
  }, []);

  useEffect(() => {
    acknowledgeNotifications();
  }, [data]);

  return (
    <>
      <NotificationListSection
        notifications={unseenNotifications}
        onNotificationClick={onNotificationClickAugmented}
        header="New"
        submitResultEvents={
          hasRecentlySeenResultSubmission ? null : submitResultEvents
        }
      />
      <NotificationListSection
        header="Earlier"
        notifications={seenNotifications}
        onNotificationClick={onNotificationClickAugmented}
        submitResultEvents={
          hasRecentlySeenResultSubmission ? submitResultEvents : null
        }
      />
      {noNotifications &&
        (loading || submitResultsLoading || !data?.user ? (
          <div className={styles.loading}>
            <LoadingEllipsis />
          </div>
        ) : (
          <div className={styles.noNotifications}>No notifications yet</div>
        ))}
    </>
  );
};

NotificationList.defaultProps = {
  onNotificationClick: () => {},
};

export default NotificationList;
